import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { BlockLifemap, LevelNumber } from '@shared/models/step.model';
import { CalendarEvent, CalendarEventTimesChangedEvent } from 'angular-calendar';
import { getRandomId } from '@app/utils/general';
import { I18nService } from '@app/i18n';
import { EventColor } from 'calendar-utils';
import {
  addDays,
  addMinutes,
  differenceInCalendarDays,
  differenceInMinutes,
  endOfDay,
  formatISO,
  isAfter,
  isBefore,
  isEqual,
  min,
  setHours,
  setMinutes,
  startOfDay,
} from 'date-fns';
import { getDateTimeNowAsString, regexpTime24h, roundToNearestHalfHour } from '@app/utils/date';
import { HttpErrorResponse } from '@angular/common/http';
import { UrlEndpoint } from '@app/utils/url';
import { LocalStorageService } from '@shared/services/localStorage.service';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { ErrorService } from '@shared/services/error.service';
import { Observable, Subscription } from 'rxjs';

export enum LifemapType {
  addNewEntry = 'addNewEntry',
  blocked = 'blocked',
  nonWork = 'non-work',
  sleep = 'sleep',
  work = 'work',
}

export type LifemapTypeBE = Exclude<LifemapType, LifemapType.blocked | LifemapType.addNewEntry>;

export function isLifemapTypeBE(type: LifemapType): type is LifemapTypeBE {
  switch (type) {
    case LifemapType.sleep:
    case LifemapType.nonWork:
    case LifemapType.work:
      return true;
    default:
      return false;
  }
}

export type AnswerValue = null | 1 | 2 | 3 | 4 | 5 | 6;

export type Answers = [
  AnswerValue,
  AnswerValue,
  AnswerValue,
  AnswerValue,
  AnswerValue,
  AnswerValue,
  AnswerValue,
  AnswerValue,
  AnswerValue,
  AnswerValue,
];

export interface LifemapLog {
  id?: number;
  timeslotId: number;
  type: LifemapTypeBE | null; // null is used by the FE to send a DELETE request, the BE should never send null back
  answers?: Answers;
  activityName?: string;
  updatedAt: string; // Format example: "2018-03-06 10:25:21"
}

export type Log = LifemapLog | null;

export interface EventMetaData {
  answers?: Answers;
  cssClassForBorder: string;
  isSelected: boolean;
  type: LifemapType;
  updatedAt: string;
}

export interface PlCalendarEvent extends CalendarEvent<EventMetaData> {
  color: EventColor;
  end: Date;
  id: string;
  meta: EventMetaData;
}

export interface StartEndDateTime {
  startDateTime: Date;
  endDateTime: Date;
}

export interface EventChanged {
  id: string | number;
  newStart: Date;
  newEnd: Date;
  answers?: Answers;
  type?: LifemapType;
  title?: string;
}

export interface PlEventColor {
  cssClassForBorder: string;
  eventColor: EventColor;
  additionalCssClass: string;
}

export const plEventsColor: Record<LifemapType, PlEventColor> = {
  [LifemapType.addNewEntry]: {
    additionalCssClass: '',
    cssClassForBorder: '',
    eventColor: {
      primary: '#E57450',
      secondary: '#E57450',
      secondaryText: '#ffffff',
    },
  },
  [LifemapType.blocked]: {
    additionalCssClass: 'pointer-events-none',
    cssClassForBorder: '',
    eventColor: {
      primary: '#ffffff',
      secondary: '#ffffff',
      secondaryText: '#515151',
    },
  },
  [LifemapType.work]: {
    additionalCssClass: '',
    cssClassForBorder: '!border-l-4 !border-l-[#55D7CD]',
    eventColor: {
      primary: '#D8F3F2',
      secondary: '#D8F3F2',
      secondaryText: '#515151',
    },
  },
  [LifemapType.nonWork]: {
    additionalCssClass: '',
    cssClassForBorder: '!border-l-4 !border-[#FF9F82]',
    eventColor: {
      primary: '#FAE3DC',
      secondary: '#FAE3DC',
      secondaryText: '#515151',
    },
  },
  [LifemapType.sleep]: {
    additionalCssClass: '',
    cssClassForBorder: '!border-l-4 !border-[#405A8A]',
    eventColor: {
      primary: '#E8EBF0',
      secondary: '#E8EBF0',
      secondaryText: '#515151',
    },
  },
};

interface DateToTimeSlotId {
  [date: string]: number;
}

export interface TimeSlotIdToLifemapLog {
  [timeSlotId: number]: LifemapLog;
}

@Injectable({
  providedIn: 'root',
})
export class LifemapService {
  private _timeSlotIdToLifeMapLog: TimeSlotIdToLifemapLog = {};
  private _dateToTimeSlotId: DateToTimeSlotId = {};

  private readonly START_DATE_AS_STRING: string = `2024-01-01`;
  public readonly NUMBER_DAYS_LIFEMAP: number = 4;
  public readonly TOTAL_NUMBER_POSSIBLE_LIFEMAP_LOGS: number = this.NUMBER_DAYS_LIFEMAP * 24 * 2;

  private readonly KEY_LOCAL_STORAGE_UNSAVED_LIFEMAPLOG: string = 'unsavedLifemapLogs';

  private _unsavedLifemapLogsSyncedWithLocalStorage: WritableSignal<LifemapLog[]> = signal([]);

  private currentSubscriptionSaveLifemapLogs: Subscription | null = null;

  constructor(
    private httpService: HttpService,
    private errorService: ErrorService,
    private i18nService: I18nService,
    private localStorageService: LocalStorageService,
    private participantService: ParticipantService,
  ) {
    effect(
      () => {
        this.participantService.participant();

        const unsavedLifemapLogsInLocalStorage: LifemapLog[] = JSON.parse(
          this.localStorageService.getFromLocalStorage(this.KEY_LOCAL_STORAGE_UNSAVED_LIFEMAPLOG) || '[]',
        );
        this._unsavedLifemapLogsSyncedWithLocalStorage.set(unsavedLifemapLogsInLocalStorage);
      },
      { allowSignalWrites: true },
    );
  }

  public resetLifemapData(): void {
    this._timeSlotIdToLifeMapLog = {};
  }

  public retrieveParticipantLifeMap(block: BlockLifemap): Observable<PlCalendarEvent[]> {
    this.resetLifemapData();

    return new Observable<PlCalendarEvent[]>((observer): void => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.GET,
        url: `${UrlEndpoint.lifemapLogsPrefix}/${block.data.config.lifeMapNumber}`,
      };

      this.httpService.request(requestOptions).subscribe({
        next: (lifemapLogs: LifemapLog[]) => {
          const lifemapLogsWithoutUnwantedTimeSlotId: LifemapLog[] = this.filterUnexpectedTimeSlotId(lifemapLogs);
          this.populateTimeSlotIdToLifemapLog(lifemapLogsWithoutUnwantedTimeSlotId, []);

          const unsavedLifemapLogsFromLocalStorage: LifemapLog[] = this._unsavedLifemapLogsSyncedWithLocalStorage();
          const lifemapLogsMerged: LifemapLog[] = this.mergeLifemapLogsFromServerAndLocalStorage(
            lifemapLogsWithoutUnwantedTimeSlotId,
            unsavedLifemapLogsFromLocalStorage,
          );
          const lifemapLogsMergedAndSorted: LifemapLog[] = this.sortLifemapLogsByTimeSlotId(lifemapLogsMerged);

          observer.next(this.convertLifemapLogsToCalendarEvents(lifemapLogsMergedAndSorted));
          observer.complete();
        },
        error: (error: HttpErrorResponse) => {
          console.error(`Error while retrieving lifemap data: error=`, error);
        },
      });
    });
  }

  private filterUnexpectedTimeSlotId(lifemapLogs: LifemapLog[]): LifemapLog[] {
    return lifemapLogs.filter((log: LifemapLog): boolean => {
      return log.timeslotId >= 0 && log.timeslotId < this.TOTAL_NUMBER_POSSIBLE_LIFEMAP_LOGS;
    });
  }

  private sortLifemapLogsByTimeSlotId(lifemapLogs: LifemapLog[]): LifemapLog[] {
    return lifemapLogs.sort((a: LifemapLog, b: LifemapLog): number => {
      return a.timeslotId > b.timeslotId ? 1 : -1;
    });
  }

  /**
   * The logs from LocalStorage hae "priority" as it's the logs that failed to save previously, they represent the latest
   * @param logServer
   * @param logsLocalStorage
   * @private
   */
  public mergeLifemapLogsFromServerAndLocalStorage(
    logServer: LifemapLog[],
    logsLocalStorage: LifemapLog[],
  ): LifemapLog[] {
    const timeSlotIdToServerLog: TimeSlotIdToLifemapLog = {};
    for (const log of logServer) {
      timeSlotIdToServerLog[log.timeslotId] = log;
    }

    logsLocalStorage.forEach((logLocalStorage: LifemapLog): void => {
      const logServerMatching: LifemapLog | undefined = timeSlotIdToServerLog[logLocalStorage.timeslotId];
      const logLocalStorageTimeSlotId: number = logLocalStorage.timeslotId;
      if (logServerMatching) {
        if (
          !logServerMatching.updatedAt ||
          (logServerMatching.updatedAt &&
            logLocalStorage.updatedAt &&
            logServerMatching.updatedAt < logLocalStorage.updatedAt)
        ) {
          timeSlotIdToServerLog[logLocalStorage.timeslotId] = logLocalStorage;
        }
      } else if (logLocalStorageTimeSlotId < 0 || logLocalStorageTimeSlotId > this.TOTAL_NUMBER_POSSIBLE_LIFEMAP_LOGS) {
        // We had a bug where a user had in its localstorage some timeslotId from -14 to -1. Somehow they managed to "extend" an event
        // before the start of the calendar, I could not reproduce the issue but I could do this to discard "invalid" logs,
        // so that the user can continue.
        console.error(`Invalid timeSlotId for log stored in local storage: `, logLocalStorage);
        this.errorService.notifyBugsnag(
          `Data issue, user has a log with a timeSlotId that does not exist. It is not normal`,
        );
      } else {
        if (logLocalStorage.id) {
          console.error(
            `As the server doesn't have a matching log for`,
            logLocalStorage,
            `, it should NOT have an ID (or we will later try to delete / modify a resource in the DB that doesn't exists `,
          );
          this.errorService.notifyBugsnag(
            `Data issue, there should not be an ID on logLocalStorage if there is no matching log on the server`,
          );

          // We should not need to remove the ID here as if the log doesn't exist on the server,
          // the logLocalStorage should not have an id. deleting the ID will ensure user can continue the lifemap
          // even if the data from localstorage might not be what the user expects (for reason I don't know as I don't expect to ever see that)
          // if the above error appears on bugsnag, I need to investigate
          delete logLocalStorage.id;
        }

        timeSlotIdToServerLog[logLocalStorage.timeslotId] = logLocalStorage;
      }
    });

    return Object.values(timeSlotIdToServerLog);
  }

  private populateTimeSlotIdToLifemapLog(lifemapLogsToUpdate: LifemapLog[], lifeMapLogsToDelete: LifemapLog[]): void {
    for (const logToUpdate of lifemapLogsToUpdate) {
      this._timeSlotIdToLifeMapLog[logToUpdate.timeslotId] = logToUpdate;
    }

    for (const logToDelete of lifeMapLogsToDelete) {
      delete this._timeSlotIdToLifeMapLog[logToDelete.timeslotId];
    }
  }

  private getLifeMapLogFromTimeSlotId(timeSlotId: number): LifemapLog | undefined {
    return this._timeSlotIdToLifeMapLog[timeSlotId];
  }

  public createSleepEvent(startDateTime: Date, endDateTime: Date): PlCalendarEvent {
    return {
      id: getRandomId(),
      title: '',
      start: roundToNearestHalfHour(startDateTime),
      end: this.getEndTime(endDateTime),
      color: plEventsColor[LifemapType.sleep].eventColor,
      cssClass: this.computeCssClassForPlEvent(LifemapType.sleep, ''),
      draggable: false,
      resizable: {
        beforeStart: true,
        afterEnd: true,
      },
      meta: {
        cssClassForBorder: plEventsColor[LifemapType.sleep].cssClassForBorder,
        isSelected: false,
        type: LifemapType.sleep,
        updatedAt: getDateTimeNowAsString(),
      },
    };
  }

  public createNewLifeMapEntryEvent(startDateTime: Date, endDateTime: Date): PlCalendarEvent {
    return {
      id: getRandomId(),
      title: '',
      start: roundToNearestHalfHour(startDateTime),
      end: this.getEndTime(endDateTime),
      color: plEventsColor[LifemapType.addNewEntry].eventColor,
      cssClass: this.computeCssClassForPlEvent(LifemapType.addNewEntry, ''),
      draggable: false,
      resizable: {
        beforeStart: false,
        afterEnd: false,
      },
      meta: {
        cssClassForBorder: plEventsColor[LifemapType.addNewEntry].cssClassForBorder,
        type: LifemapType.addNewEntry,
        updatedAt: getDateTimeNowAsString(),
        isSelected: false,
      },
    };
  }

  public createNewBlockedEvent(
    dayNumberToCompleteBeforeProceeding: number,
    startDateTime: Date,
    endDateTime: Date,
  ): PlCalendarEvent {
    return {
      id: getRandomId(),
      title: this.i18nService.translate(`lifemap.please_complete_day_before_proceeding`, {
        dayNumber: dayNumberToCompleteBeforeProceeding,
      }),
      start: roundToNearestHalfHour(startDateTime),
      end: this.getEndTime(endDateTime),
      color: plEventsColor[LifemapType.blocked].eventColor,
      cssClass: this.computeCssClassForPlEvent(LifemapType.blocked, ''),
      draggable: false,
      resizable: {
        beforeStart: false,
        afterEnd: false,
      },
      meta: {
        cssClassForBorder: plEventsColor[LifemapType.blocked].cssClassForBorder,
        type: LifemapType.blocked,
        updatedAt: getDateTimeNowAsString(),
        isSelected: false,
      },
    };
  }

  public computeCssClassForPlEvent(type: LifemapType, cssClassToAdd: string): string {
    const cssClassBasedOnType: string = plEventsColor[type].additionalCssClass;

    return `${cssClassBasedOnType} ${cssClassToAdd}`;
  }

  public isSameEvent(event1: PlCalendarEvent, event2: PlCalendarEvent): boolean {
    if (event1.meta?.type !== event2.meta?.type) {
      return false;
    }

    if (event1.title !== event2.title) {
      return false;
    }

    if (
      event1.meta?.answers &&
      event2.meta?.answers &&
      JSON.stringify(event1.meta?.answers) !== JSON.stringify(event2.meta?.answers)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Due to how angular-calendar renders event, an event can't end at 00:00 (otherwise, it shows on the next day)
   * Similarly, an event can't start at 23:59:59.999, it needs to start at 00:00
   * @param dateTime
   */
  public getEndTime(dateTime: Date): Date {
    const dateTimeRoundedToHalfHour: Date = roundToNearestHalfHour(dateTime);

    if (isEqual(dateTimeRoundedToHalfHour, startOfDay(dateTimeRoundedToHalfHour))) {
      return endOfDay(addDays(dateTimeRoundedToHalfHour, -1));
    }

    return dateTimeRoundedToHalfHour;
  }

  public getCalendarStartDate(): Date {
    return new Date(`${this.START_DATE_AS_STRING}T00:00:00`);
  }

  public getCalendarEndDate(): Date {
    return this.getEndTime(addDays(this.getCalendarStartDate(), this.NUMBER_DAYS_LIFEMAP));
  }

  public getDateTimeBasedOnStartDate(time: string, numberOfDaysToAdd: number = 0): Date {
    return addDays(new Date(`${this.START_DATE_AS_STRING}T${time}:00`), numberOfDaysToAdd);
  }

  public getCalendarsDaysDiff(laterDate: Date, earlierDate: Date): number {
    if (isEqual(laterDate, earlierDate)) {
      return 0;
    }

    const laterDateToUseForCompare: Date = this.getEndTime(laterDate);
    const earlierDateToUseForCompare: Date = roundToNearestHalfHour(earlierDate);
    return differenceInCalendarDays(laterDateToUseForCompare, earlierDateToUseForCompare);
  }

  public setTimeOnExistingDate(date: Date, time: string): Date {
    const found: RegExpMatchArray | null = time.match(regexpTime24h);
    if (found) {
      const hours: number = +found[1];
      const minutes: number = +found[2];
      return setMinutes(setHours(date, hours), minutes);
    } else {
      throw new Error(`Wrong format for time: ${time}, should be "HH:mm"`);
    }
  }

  public computeNewStartEndDatesBasedOnTime(eventStartDate: Date, timeFrom: string, timeTo: string): StartEndDateTime {
    const startDateTime: Date = this.setTimeOnExistingDate(eventStartDate, timeFrom);
    let endDateTime: Date = this.setTimeOnExistingDate(eventStartDate, timeTo);
    if (timeTo < timeFrom) {
      endDateTime = min([this.getCalendarEndDate(), addDays(endDateTime, 1)]);
    }

    return {
      startDateTime,
      endDateTime,
    };
  }

  public getTimeSlotIdFromDate(date: Date): number {
    const dateIso: string = formatISO(date);
    if (!this._dateToTimeSlotId[dateIso]) {
      const startDateLifemap: Date = this.getCalendarStartDate();
      const numberMinutesDiff: number = differenceInMinutes(roundToNearestHalfHour(date), startDateLifemap);
      const remainder: number = numberMinutesDiff % 30;
      if (remainder === 0) {
        this._dateToTimeSlotId[dateIso] = numberMinutesDiff / 30;
      } else {
        console.error(
          `getTimeSlotIdFromDate() - startDateLifemap=`,
          startDateLifemap,
          '   date=',
          date,
          '   numberMinutesDiff=',
          numberMinutesDiff,
          '   remainder=',
          remainder,
        );
        throw new Error(`the remainder should be 0`);
      }
    }

    return this._dateToTimeSlotId[dateIso];
  }

  public convertEventsToLifeMapLogs(events: PlCalendarEvent[]): LifemapLog[] {
    this.logEvents(events, 'LifemapService convertEventsToLifeMapLogsAndSave events');
    const lifeMapLogFromEvents: LifemapLog[] = [];
    if (events.length > 0) {
      for (const event of events) {
        const startTimeSlotId: number = this.getTimeSlotIdFromDate(event.start);
        const endTimeSlotId: number = this.getTimeSlotIdFromDate(event.end);
        for (let timeslotId: number = startTimeSlotId; timeslotId < endTimeSlotId; timeslotId++) {
          const lifemapLog: LifemapLog | undefined = this.getLifeMapLogFromTimeSlotId(timeslotId);
          if (isLifemapTypeBE(event.meta.type)) {
            const lifemapLogUpdated: LifemapLog = {
              id: lifemapLog?.id,
              timeslotId,
              type: event.meta.type,
              answers: this.getAnswersForLifemapLogs(event),
              activityName: this.getActivityNameForLifemapLogs(event),
              updatedAt: event.meta.updatedAt,
            };
            lifeMapLogFromEvents.push(lifemapLogUpdated);
          } else if (lifemapLog?.id !== undefined) {
            lifeMapLogFromEvents.push({
              id: lifemapLog.id,
              timeslotId,
              type: null,
              updatedAt: event.meta.updatedAt,
            });
          }
        }
      }
    }
    return lifeMapLogFromEvents;
  }

  private getAnswersForLifemapLogs(event: PlCalendarEvent): Answers {
    if (event.meta.type === LifemapType.sleep) {
      return [null, null, null, null, null, null, null, null, null, null];
    } else if (event.meta.answers) {
      return event.meta.answers;
    }

    console.error(`Answers not defined and it is not a sleep event, not normal, event=`, event);
    throw new Error(`answers should be defined or it is a sleep event, Unknown bug here`);
  }

  private getActivityNameForLifemapLogs(event: PlCalendarEvent): string {
    if (event.meta.type === LifemapType.sleep) {
      return 'Sleep';
    }

    return event.title;
  }

  public async saveChangedLifemapLogs(allLifemapLogsFromEvents: LifemapLog[], level: LevelNumber): Promise<boolean> {
    const lifemapLogWithoutUnchangedLogs: LifemapLog[] = this.filterUnchangedLogs(allLifemapLogsFromEvents);

    if (lifemapLogWithoutUnchangedLogs.length === 0) {
      return Promise.resolve(true);
    }

    const requestOptions: HttpQueryInterface = {
      type: HttpRequestType.PUT,
      url: `${UrlEndpoint.lifemapLogsPrefix}/${level}`,
      body: lifemapLogWithoutUnchangedLogs,
    };

    // if the below endpoint returns 401, user will be redirected to login page and the participant will be reset
    //  => meaning we won't be able to save the lifemap log as we won't have a participantId anymore
    // With this trick we can save the participant lifemap change so that after login, he will still have his data
    const savedParticipantId: number | undefined = this.participantService.participant()?.id;

    return new Promise<boolean>((resolve): void => {
      if (this.currentSubscriptionSaveLifemapLogs) {
        this.currentSubscriptionSaveLifemapLogs.unsubscribe();
      }
      this.currentSubscriptionSaveLifemapLogs = this.httpService.request(requestOptions).subscribe({
        next: (lifemapLogs: LifemapLog[]): void => {
          this.clearUnsavedLifemapLogInLocalStorage();
          const lifemapLogDeleted: LifemapLog[] = lifemapLogWithoutUnchangedLogs.filter(
            (log: LifemapLog): boolean => log.type === null,
          );
          this.populateTimeSlotIdToLifemapLog(lifemapLogs, lifemapLogDeleted);
          resolve(true);
        },
        error: (error: HttpErrorResponse): void => {
          console.error(`Error while saving lifemap data: error=`, error);
          if (error.status !== 401) {
            this.errorService.notifyBugsnag(`Error while saving lifemap data`);
          }
          this.storeUnsavedLifemapLogInLocalStorage(lifemapLogWithoutUnchangedLogs, savedParticipantId);
          resolve(false);
        },
      });
    });
  }

  private filterUnchangedLogs(currentLifeMapLogs: LifemapLog[]): LifemapLog[] {
    return currentLifeMapLogs.filter((currentLog: LifemapLog): boolean => {
      if (currentLog.id === undefined) {
        // Log to create => keep it
        return true;
      } else if (currentLog.type === null) {
        // Log to delete => keep it
        return true;
      } else {
        const matchingSavedLog: LifemapLog | undefined = this.getLifeMapLogFromTimeSlotId(currentLog.timeslotId);
        if (matchingSavedLog) {
          return !this.areLogsIdentical(currentLog, matchingSavedLog);
        } else {
          throw new Error(`We should always find a matching log if we don't create it`);
        }
      }
    });
  }

  public convertLifemapLogsToCalendarEvents(lifemapLogs: LifemapLog[]): PlCalendarEvent[] {
    const events: PlCalendarEvent[] = [];
    let firstPreviousLogIdentical: Log = null;
    let lastPreviousLogIdentical: Log = null;

    // The only scenario where we will have log.type === null is when user lost connection, reduce the duration of an entry
    //  => that creates log with "null" type in order to send a delete request to the back end. Next sync with the back end
    //     will send the appropriate delete requests
    const lifeMapLogsWithoutNullType: LifemapLog[] = lifemapLogs.filter((log: LifemapLog): boolean => {
      return log.type !== null;
    });

    for (const log of lifeMapLogsWithoutNullType) {
      if (this.areLogsIdenticalNotNullAndFollowingEachOther(lastPreviousLogIdentical, log)) {
        lastPreviousLogIdentical = log;
      } else {
        if (firstPreviousLogIdentical !== null && lastPreviousLogIdentical !== null) {
          events.push(this.getEventFromIdenticalLogs(firstPreviousLogIdentical, lastPreviousLogIdentical));
        }

        firstPreviousLogIdentical = log;
        lastPreviousLogIdentical = log;
      }
    }

    if (firstPreviousLogIdentical !== null && lastPreviousLogIdentical !== null) {
      events.push(this.getEventFromIdenticalLogs(firstPreviousLogIdentical, lastPreviousLogIdentical));
    }

    return events;
  }

  public areLogsIdenticalNotNullAndFollowingEachOther(previousLog: Log, nextLog: Log): boolean {
    if (previousLog === null || nextLog === null) {
      return false;
    } else if (previousLog.timeslotId + 1 !== nextLog.timeslotId) {
      return false;
    }

    return this.areLogsIdentical(previousLog, nextLog);
  }

  public areLogsIdentical(previousLog: LifemapLog, nextLog: LifemapLog): boolean {
    if (previousLog.type !== nextLog.type || previousLog.activityName !== nextLog.activityName) {
      return false;
    } else if (previousLog.type === LifemapType.sleep && nextLog.type === LifemapType.sleep) {
      return true;
    } else if (
      previousLog.answers &&
      nextLog.answers &&
      JSON.stringify(previousLog.answers) !== JSON.stringify(nextLog.answers)
    ) {
      return false;
    }

    return true;
  }

  private getEventFromIdenticalLogs(firstLog: LifemapLog, lastLog: LifemapLog): PlCalendarEvent {
    const startDate: Date = this.getDateTimeFromTimeSlotId(firstLog.timeslotId, false);
    const endDate: Date = this.getDateTimeFromTimeSlotId(lastLog.timeslotId, true);

    if (lastLog.type) {
      const metaData: EventMetaData = {
        cssClassForBorder: plEventsColor[lastLog.type].cssClassForBorder,
        answers: lastLog.answers ? [...lastLog.answers] : undefined,
        isSelected: false,
        type: lastLog.type,
        updatedAt: lastLog.updatedAt,
      };

      return {
        id: getRandomId(),
        title: this.getEventTitleFromLog(lastLog),
        start: startDate,
        end: endDate,
        color: plEventsColor[lastLog.type].eventColor,
        cssClass: this.computeCssClassForPlEvent(lastLog.type, ''),
        draggable: false,
        resizable: {
          beforeStart: true,
          afterEnd: true,
        },
        meta: metaData,
      };
    } else {
      console.error(`getEventFromIdenticalLogs(), lifemapType should not be null, lastLog=`, lastLog);
      throw new Error(`LifemapType should not be null`);
    }
  }

  private getDateTimeFromTimeSlotId(timeSlotId: number, isEndTime: boolean): Date {
    const startDateLifemap: Date = this.getCalendarStartDate();
    const numberOf30MinSlots: number = isEndTime ? timeSlotId + 1 : timeSlotId;
    const dateTimeMatchingTimeslotId: Date = addMinutes(startDateLifemap, 30 * numberOf30MinSlots);

    return isEndTime ? this.getEndTime(dateTimeMatchingTimeslotId) : dateTimeMatchingTimeslotId;
  }

  public getEventTitleFromLog(log: LifemapLog): string {
    if (log.type && [LifemapType.work, LifemapType.nonWork].includes(log.type)) {
      return log.activityName || '';
    }

    return '';
  }

  public getActivitiesFromEvents(events: PlCalendarEvent[]): string[] {
    const activitiesSet: Set<string> = new Set<string>();

    const workAndNonWorkEvents: PlCalendarEvent[] = events.filter((event: PlCalendarEvent): boolean => {
      return event.meta?.type === LifemapType.work || event.meta?.type === LifemapType.nonWork;
    });

    workAndNonWorkEvents.forEach((event: PlCalendarEvent): void => {
      activitiesSet.add(event.title);
    });

    return Array.from(activitiesSet);
  }

  public applyEventChangeWithoutComputation(
    eventChanged: CalendarEventTimesChangedEvent<EventMetaData>,
    oldEvents: PlCalendarEvent[],
  ): PlCalendarEvent[] {
    return oldEvents.map((event: PlCalendarEvent): PlCalendarEvent => {
      if (event.id === eventChanged.event.id) {
        return {
          ...event,
          start: eventChanged.newStart,
          end: eventChanged.newEnd as Date,
        };
      } else {
        return {
          ...event,
        };
      }
    });
  }

  public applyEventChangeWithComputation(eventChanged: EventChanged, oldEvents: PlCalendarEvent[]): PlCalendarEvent[] {
    const updatedEvents: PlCalendarEvent[] = [];

    for (const currentEvent of oldEvents) {
      if (currentEvent.end) {
        if (currentEvent && eventChanged.id === currentEvent.id) {
          updatedEvents.push(this.getUpdatedEventFromEventChanged(currentEvent, eventChanged));
        } else {
          if (isBefore(currentEvent.start, eventChanged.newStart)) {
            // CurrentEvent starts before eventChanged starts
            if (isBefore(currentEvent.end, eventChanged.newStart) || isEqual(currentEvent.end, eventChanged.newStart)) {
              // CurrentEvent finishes before eventChanged starts: KEEP IT untouched
              updatedEvents.push({ ...currentEvent });
            } else if (
              isBefore(currentEvent.end, eventChanged.newEnd) ||
              isEqual(currentEvent.end, eventChanged.newEnd)
            ) {
              // CurrentEvent finishes before eventChanged finish: update endTime to be the start of eventChanged
              updatedEvents.push({ ...currentEvent, end: this.getEndTime(eventChanged.newStart) });
            } else {
              // CurrentEvent finishes after eventChanged starts: SPLIT IT IN TWO, copy it and
              //  - First: update endTime to be the start of eventChanged
              //  - Second: update startTime to be the end of eventChanged
              updatedEvents.push({ ...currentEvent, end: this.getEndTime(eventChanged.newStart) });
              updatedEvents.push({
                ...currentEvent,
                id: getRandomId(),
                start: roundToNearestHalfHour(eventChanged.newEnd),
              });
            }
          } else {
            // CurrentEvent starts after eventChanged starts
            if (isAfter(currentEvent.start, eventChanged.newEnd)) {
              // CurrentEvent starts after eventChanged finishes: KEEP IT untouched
              updatedEvents.push({ ...currentEvent });
            } else if (isAfter(currentEvent.end, eventChanged.newEnd)) {
              // CurrentEvent ends before eventChanged finishes: KEEP IT and update startTime to be the end of eventChanged
              updatedEvents.push({ ...currentEvent, start: roundToNearestHalfHour(eventChanged.newEnd) });
            } else {
              // CurrentEvent starts after eventChanged start & finish before eventChanged ends
              // => CurrentEvent is completely overridden: REMOVE IT (don't add it in the new array => do nothing)
            }
          }
        }
      }
    }

    return this.fillGapsWithSpecialEventsAndSortAndMergeSiblings(updatedEvents);
  }

  public getUpdatedEventFromEventChanged(event: PlCalendarEvent, eventChanged: EventChanged): PlCalendarEvent {
    const eventUpdated: PlCalendarEvent = {
      ...event,
      start: roundToNearestHalfHour(eventChanged.newStart),
      end: this.getEndTime(eventChanged.newEnd),
    };

    if (eventChanged.title !== undefined) {
      eventUpdated.title = eventChanged.title;
    }

    if (eventUpdated.meta) {
      if (eventChanged.answers !== undefined) {
        eventUpdated.meta.answers = eventChanged.answers;
      }

      if (eventChanged.type !== undefined) {
        eventUpdated.meta.type = eventChanged.type;
        eventUpdated.color = plEventsColor[eventChanged.type].eventColor;
        eventUpdated.resizable = {
          beforeStart: true,
          afterEnd: true,
        };
      }
    }

    return eventUpdated;
  }

  public sortEventByDates(events: PlCalendarEvent[]): PlCalendarEvent[] {
    return [...events].sort((event1: PlCalendarEvent, event2: PlCalendarEvent): number => {
      return event1 && event2 && isBefore(event1.start, event2.start) ? -1 : 1;
    });
  }

  public fillGapsWithSpecialEventsAndSortAndMergeSiblings(events: PlCalendarEvent[]): PlCalendarEvent[] {
    const eventsSorted: PlCalendarEvent[] = this.sortEventByDates(events);

    // As we have to go through all the events anyway, it's easier to remove all the special events to always have a "clean" start
    const eventsSortedWithoutAnySpecialEvents: PlCalendarEvent[] = eventsSorted.filter(
      (event: PlCalendarEvent): boolean => {
        return isLifemapTypeBE(event.meta.type);
      },
    );

    let previousEventEndDate: Date = this.getCalendarStartDate();
    const eventsToAdd: PlCalendarEvent[] = [];
    let dateTimeStartFirstNewLifeMapEntry: Date | null = null;

    for (const currentEvent of eventsSortedWithoutAnySpecialEvents) {
      if (
        isBefore(previousEventEndDate, currentEvent.start) &&
        differenceInMinutes(currentEvent.start, previousEventEndDate) >= 30 // exclude gaps between 23:59:59 & 00:00:00
      ) {
        eventsToAdd.push(
          ...this.getSpecialEventsBetweenDates(
            previousEventEndDate,
            currentEvent.start,
            dateTimeStartFirstNewLifeMapEntry,
          ),
        );

        if (!dateTimeStartFirstNewLifeMapEntry) {
          dateTimeStartFirstNewLifeMapEntry = previousEventEndDate;
        }
      }

      previousEventEndDate = currentEvent.end as Date;
      eventsToAdd.push(currentEvent);
    }

    const endOfCalendarDate: Date = this.getCalendarEndDate();
    if (previousEventEndDate && isBefore(previousEventEndDate, endOfCalendarDate)) {
      eventsToAdd.push(
        ...this.getSpecialEventsBetweenDates(
          previousEventEndDate,
          endOfCalendarDate,
          dateTimeStartFirstNewLifeMapEntry,
        ),
      );
    }

    const updatedEventsSorted: PlCalendarEvent[] = this.sortEventByDates(eventsToAdd);
    return this.mergeSiblingsIdenticalEvents(updatedEventsSorted);
  }

  private getSpecialEventsBetweenDates(
    earlierDate: Date,
    laterDate: Date,
    dateTimeStartFirstNewLifeMapEntry: Date | null,
  ): PlCalendarEvent[] {
    const eventsToAdd: PlCalendarEvent[] = [];

    if (
      dateTimeStartFirstNewLifeMapEntry &&
      this.getCalendarsDaysDiff(laterDate, dateTimeStartFirstNewLifeMapEntry) > 0
    ) {
      const dayDiff: number = this.getCalendarsDaysDiff(dateTimeStartFirstNewLifeMapEntry, this.getCalendarStartDate());
      eventsToAdd.push(this.createNewBlockedEvent(dayDiff + 1, earlierDate, laterDate));
    } else {
      if (this.getCalendarsDaysDiff(laterDate, earlierDate) > 0) {
        eventsToAdd.push(this.createNewLifeMapEntryEvent(earlierDate, endOfDay(earlierDate)));
        const dayDiff: number = this.getCalendarsDaysDiff(
          dateTimeStartFirstNewLifeMapEntry || earlierDate,
          this.getCalendarStartDate(),
        );

        for (
          let dayNumberToAdd: number = 1;
          dayNumberToAdd <= this.getCalendarsDaysDiff(laterDate, earlierDate);
          dayNumberToAdd++
        ) {
          const startDateForBlockEvent: Date = addDays(startOfDay(earlierDate), dayNumberToAdd);
          const endDateForBlockEvent: Date = min([addDays(endOfDay(earlierDate), dayNumberToAdd), laterDate]);
          eventsToAdd.push(this.createNewBlockedEvent(dayDiff + 1, startDateForBlockEvent, endDateForBlockEvent));
        }
      } else {
        eventsToAdd.push(this.createNewLifeMapEntryEvent(earlierDate, laterDate));
      }
    }

    return eventsToAdd;
  }

  public getDayNumberForEvent(event: PlCalendarEvent): number {
    const eventStartDate: Date = event.start;

    // Add 20 min as if the event starts at 00:00, the diff in calendar days won't count this event on the "next" day
    // Hack: we need to add more than 15min as "getCalendarsDaysDiff()" will call this.getEndTime(eventStartDayPlus20min)
    //  => and this will return 23:59:59 on the previous day (for other valid reasons) if the date is between 00:00:00 & 00:15:00
    const eventStartDayPlus20min: Date = addMinutes(eventStartDate, 20);
    const dayDiff: number = this.getCalendarsDaysDiff(eventStartDayPlus20min, this.getCalendarStartDate());

    return dayDiff;
  }

  public getDayNumberForFirstAddNewEntry(events: PlCalendarEvent[]): number {
    const firstAddNewEntryEvent: PlCalendarEvent | null = this.getFirstAddNewEntryEvent(events);
    if (firstAddNewEntryEvent) {
      return this.getDayNumberForEvent(firstAddNewEntryEvent);
    }

    return this.NUMBER_DAYS_LIFEMAP;
  }

  public getFirstAddNewEntryEvent(events: PlCalendarEvent[]): PlCalendarEvent | null {
    for (const event of events) {
      if (event.meta.type === LifemapType.addNewEntry) {
        return event;
      }
    }

    return null;
  }

  public getLastAddNewEntryEvent(events: PlCalendarEvent[]): PlCalendarEvent | null {
    for (const event of events.slice().reverse()) {
      if (event.meta.type === LifemapType.addNewEntry) {
        return event;
      }
    }

    return null;
  }

  private mergeSiblingsIdenticalEvents(events: PlCalendarEvent[]): PlCalendarEvent[] {
    const updatedEvents: PlCalendarEvent[] = [];
    let previousEvent: PlCalendarEvent | undefined;
    for (let index: number = 0; index < events.length; index++) {
      const currentEvent: PlCalendarEvent = events[index];
      if (!previousEvent) {
        updatedEvents.push(currentEvent);
        previousEvent = currentEvent;
      } else {
        if (this.isSameEvent(currentEvent, previousEvent)) {
          previousEvent.end = currentEvent.end;
        } else {
          updatedEvents.push(currentEvent);
          previousEvent = currentEvent;
        }
      }
    }

    return updatedEvents;
  }

  public getEventsOverlappingWithEventUpdated(
    eventChanged: EventChanged,
    allEvents: PlCalendarEvent[],
  ): PlCalendarEvent[] {
    const eventsOverridden: PlCalendarEvent[] = [];
    for (const event of allEvents) {
      if (event.end && event.id !== eventChanged.id && isLifemapTypeBE(event.meta.type)) {
        if (isBefore(eventChanged.newStart, event.end) && isAfter(eventChanged.newEnd, event.start)) {
          eventsOverridden.push(event);
        }

        if (isBefore(eventChanged.newEnd, event.start)) {
          break;
        }
      }
    }

    return eventsOverridden;
  }

  public deepCopyEvents(eventsToCopy: PlCalendarEvent[]): PlCalendarEvent[] {
    return eventsToCopy.map((event: PlCalendarEvent): PlCalendarEvent => {
      return this.deepCopyEvent(event);
    });
  }

  public deepCopyEvent(eventsToCopy: PlCalendarEvent): PlCalendarEvent {
    return {
      ...eventsToCopy,
      meta: eventsToCopy.meta && {
        ...eventsToCopy.meta,
      },
    };
  }

  public logEvents(events: PlCalendarEvent[], logTitle: string = ''): void {
    const deepCopyEvents: PlCalendarEvent[] = this.deepCopyEvents(events);
    for (const event of deepCopyEvents) {
      if (event.meta.type === LifemapType.addNewEntry) {
        event.title = 'Create NEW';
      } else if (event.meta.type === LifemapType.blocked) {
        event.title = 'BLOCKED';
      } else if (event.meta.type === LifemapType.sleep) {
        event.title = 'SLEEP';
      }
    }

    console.log(`TONIO LifemapService logEvent ${logTitle}=`, deepCopyEvents);
  }

  private storeUnsavedLifemapLogInLocalStorage(
    lifemapLogs: LifemapLog[],
    overriddenParticipantId: number | undefined,
  ): void {
    const lifemapLogsUpdated: LifemapLog[] = lifemapLogs.map((log: LifemapLog): LifemapLog => {
      return {
        ...log,
        updatedAt: getDateTimeNowAsString(),
      };
    });
    this._unsavedLifemapLogsSyncedWithLocalStorage.set(lifemapLogsUpdated);
    const valueToStore: string = JSON.stringify(lifemapLogsUpdated);
    this.localStorageService.storeInLocalStorage(
      this.KEY_LOCAL_STORAGE_UNSAVED_LIFEMAPLOG,
      valueToStore,
      overriddenParticipantId,
    );
  }

  private clearUnsavedLifemapLogInLocalStorage(): void {
    this._unsavedLifemapLogsSyncedWithLocalStorage.set([]);
    this.localStorageService.removeFromLocalStorage(this.KEY_LOCAL_STORAGE_UNSAVED_LIFEMAPLOG);
  }

  public isLocalCalendarFullyCompleted(events: PlCalendarEvent[]): boolean {
    for (const event of events) {
      if (event.meta.type === LifemapType.blocked || event.meta.type === LifemapType.addNewEntry) {
        return false;
      }
    }

    return true;
  }

  public isLifemapLogsFullyCompleted(): boolean {
    const numberOfLifeMapLogsStoredLocally: number = Object.values(this._timeSlotIdToLifeMapLog).length;
    const totalNumberOfPossibleLifeMapLogs: number = this.TOTAL_NUMBER_POSSIBLE_LIFEMAP_LOGS;
    return numberOfLifeMapLogsStoredLocally === totalNumberOfPossibleLifeMapLogs;
  }
}
